import moment from 'moment-timezone';
import { isEmpty, omit, trim, uniq } from 'lodash';

import {
  IMPORT_BLOCKED_HTML_TAGS,
  IMPORT_CHANNEL,
  IMPORT_DATE_FORMAT,
  IMPORT_DATE_FORMAT_24H,
  IMPORT_ERROR_ALIASES,
  IMPORT_ERRORS,
  IMPORT_MEDIA_TYPES,
  IMPORT_REVIEW_COLUMNS,
  IMPORT_ROW_STATUS,
  IMPORT_SERVER_STATUS,
  IMPORT_VALIDATED_FIELDS,
} from 'constants/announcementImport';

/**
 * Validation for the bulk import review.
 *
 * Rows use the import API's field names. Each field validates on its own so a review cell only reads
 * (and so only re-renders for) the fields that decide its own error.
 *
 * The API validates first and returns per-field `errors`; those are a row's starting state. Editing a
 * field clears its server error and client validation takes over. `context` carries what a row can't
 * know by itself: `now` (for future dates), `serverErrors` (the row's remaining API errors),
 * `regionCodes` (from the regions API), `crewsById` (the driver crews loaded so far, as
 * id -> { id, name, region, driverCount }) and `poolDriversById` (each pool's drivers per region code,
 * id -> Map(REGION -> count)). Anything missing just skips the check that needs it.
 *
 * Drivers: a row with a Date & Time must reach someone, by driver IDs or by a pool or crew whose
 * drivers (in the row's regions, for pools) add up to more than 0. See validateDriverReach.
 */

// Server errors with no column are keyed here, or by fields the grid doesn't show.
export const ROW_ERROR_KEY = '_row';

const TEXT_FIELDS = ['name', 'subject', 'description', 'communication', 'origin_schedule_time', 'origin_driver_pools', 'origin_driver_crews'];

const CHANNELS = IMPORT_MEDIA_TYPES.map((option) => option.value);
const DATE_FORMATS = [
  { pattern: /^\d{2}\/\d{2}\/\d{4} (0[1-9]|1[0-2]):[0-5]\d [AP]M$/i, format: IMPORT_DATE_FORMAT },
  { pattern: /^\d{2}\/\d{2}\/\d{4} ([01]\d|2[0-3]):[0-5]\d$/, format: IMPORT_DATE_FORMAT_24H },
];
const DRIVER_ID_PATTERN = /^\d+$/;
const BLOCKED_HTML_PATTERN = new RegExp(`<\\s*(${IMPORT_BLOCKED_HTML_TAGS.join('|')})(?=[\\s/>])`, 'gi');

// Every key the API may file a field's errors under: the field itself and its aliases.
export const getErrorKeys = (field) => [field, ...(IMPORT_ERROR_ALIASES[field] || [])];

// Error keys with a cell to show them in; the first column is the status chip, not a field.
const COLUMN_ERROR_KEYS = IMPORT_REVIEW_COLUMNS.filter((column) => column.key !== 'status').flatMap((column) =>
  getErrorKeys(column.field || column.key),
);

const normalizeChannel = (channel) => {
  const value = trim(channel);
  return CHANNELS.find((option) => option.toLowerCase() === value.toLowerCase()) || value;
};

// Splits a comma-separated cell (or an API array) into trimmed, non-empty entries.
const splitList = (value) => (Array.isArray(value) ? value : String(value || '').split(',')).map((entry) => trim(entry)).filter(Boolean);

const findRegionCode = (entry, regionCodes) => regionCodes.find((code) => code.toLowerCase() === entry.toLowerCase());

// Driver regions, a comma-separated list
export const parseRegions = (value, regionCodes) => {
  const entries = splitList(value);
  if (!regionCodes) {
    return { codes: entries, invalid: [] };
  }
  return {
    codes: entries.map((entry) => findRegionCode(entry, regionCodes)).filter(Boolean),
    invalid: entries.filter((entry) => !findRegionCode(entry, regionCodes)),
  };
};

// For editing: each known region in its code's casing, unknown ones (or all, without codes) as typed.
// Driver regions are kept as an array of codes; a comma-separated string is still split.
const normalizeRegions = (value, regionCodes) => splitList(value).map((entry) => (regionCodes && findRegionCode(entry, regionCodes)) || entry);

// "Invalid region code(s): MARS, XYZ." for the codes the regions API didn't return.
export const getInvalidRegionMessage = (invalidCodes) => `${IMPORT_ERRORS.INVALID_REGION} ${invalidCodes.join(', ')}.`;

const toIdList = (value) => (Array.isArray(value) ? value : splitList(value));

// Sheet names the API couldn't match stay on the row (client-only) until removed in the cell.
export const NOT_FOUND_FIELDS = { driver_pool_ids: 'not_found_driver_pools', driver_crew_ids: 'not_found_driver_crews' };

// Pools or crews the row lists: picked by id, or sheet names the API couldn't match.
const hasGroups = (row) =>
  Object.entries(NOT_FOUND_FIELDS)
    .flat()
    .some((field) => toIdList(row[field]).length);

// Any entry, a malformed one included (the Driver IDs cell has its own error for that).
export const hasDriverIds = (row) => splitList(row.drivers).length > 0;

// A region is required for the pools and crews, unless driver IDs take their place.
const validateDriverRegions = (row, regionCodes) => {
  if (!splitList(row.driver_regions).length) {
    return hasGroups(row) && !hasDriverIds(row) ? IMPORT_ERRORS.REGION_REQUIRED : null;
  }
  const { invalid } = parseRegions(row.driver_regions, regionCodes);
  return invalid.length ? getInvalidRegionMessage(invalid) : null;
};

const inRegions = (region, regions) => regions.some((code) => code.toLowerCase() === String(region || '').toLowerCase());

// The selected pools or crews (by id) whose region isn't one of the row's. Ids not loaded yet are
// skipped: nothing is known about them until their list arrives.
export const getOutsideRegion = (ids, regions, byId) =>
  toIdList(ids)
    .map((id) => byId && byId.get(id))
    .filter((item) => item && !inRegions(item.region, regions));

const validateOutsideRegions = (row, field, byId, prefix) => {
  const outside = getOutsideRegion(row[field], splitList(row.driver_regions), byId);
  return outside.length ? `${prefix} ${outside.map((item) => item.name).join(', ')}.` : null;
};

// Sheet names the API couldn't match, worded as the API words them.
const validateNotFound = (row, field, prefix) => {
  const notFound = toIdList(row[NOT_FOUND_FIELDS[field]]);
  return notFound.length ? `${prefix} ${notFound.join(', ')}` : null;
};

// The names a sheet cell listed, e.g. "Weekend Warriors, Night Shift"; shown until the options load.
export const parseOriginNames = (value) => splitList(value);

// Bulk api reads comma separated value from file.
// For driver pools and driver crews, api returns 3 seperated fields related
// origin is comma separated user fill in file
// ids is the ids of pools/crews that server found by origin value
// messages are the errors that api returns which origin is not found
// => must to pair to show on review table that which one is not found as a autocomplete option
export const pairOriginNames = (origin, ids, messages = []) => {
  const names = parseOriginNames(origin);
  const pairs = (found) => Object.fromEntries(ids.map((id, index) => [String(id), found[index]]));
  if (!ids.length) {
    return { names: {}, unmatched: names };
  }
  if (names.length === ids.length) {
    return { names: pairs(names), unmatched: [] };
  }
  const found = names.filter((name) => !messages.some((message) => message.includes(name)));
  if (found.length !== ids.length) {
    return { names: {}, unmatched: [] };
  }
  return { names: pairs(found), unmatched: names.filter((name) => !found.includes(name)) };
};

export const matchOriginNames = (origin, ids, messages) => pairOriginNames(origin, ids, messages).names;

const toText = (value) => (value === null || value === undefined ? '' : String(value));

export const normalizeImportRow = (data, { regionCodes } = {}) => ({
  ...data,
  ...TEXT_FIELDS.reduce((fields, field) => ({ ...fields, [field]: toText(data[field]) }), {}),
  media_type: normalizeChannel(data.media_type),
  schedule_timezone: trim(data.schedule_timezone),
  driver_regions: normalizeRegions(data.driver_regions, regionCodes),
  driver_pool_ids: toIdList(data.driver_pool_ids),
  driver_crew_ids: toIdList(data.driver_crew_ids),
  not_found_driver_pools: toIdList(data.not_found_driver_pools),
  not_found_driver_crews: toIdList(data.not_found_driver_crews),
  drivers: Array.isArray(data.drivers) ? data.drivers.join(', ') : toText(data.drivers),
});

// The API's `errors` for a row, as { [field_name]: [messages] }. A row marked ERROR without any field error
// still needs fixing, so it gets a row-level error that any edit clears.
export const toServerErrors = ({ status, errors }) => {
  const fieldErrors = { ...(errors || {}) };
  if (status === IMPORT_SERVER_STATUS.ERROR && isEmpty(fieldErrors)) {
    fieldErrors[ROW_ERROR_KEY] = [IMPORT_ERRORS.SERVER_REJECTED];
  }
  return fieldErrors;
};

export const parseDriverIds = (value) => {
  const entries = splitList(value);
  return {
    ids: entries.filter((entry) => DRIVER_ID_PATTERN.test(entry)),
    invalid: entries.filter((entry) => !DRIVER_ID_PATTERN.test(entry)),
  };
};

export const isSubjectApplicable = (channel) => channel !== IMPORT_CHANNEL.SMS;

export const getSubjectLabel = (channel) => (channel === IMPORT_CHANNEL.MESSENGER ? 'Title' : 'Subject');

const isValidTimezone = (timezone) => !!timezone && !!moment.tz.zone(timezone);

const validateSchedule = (row, now) => {
  if (!trim(row.origin_schedule_time)) {
    return null;
  }
  const schedule = trim(row.origin_schedule_time);
  const dateFormat = DATE_FORMATS.find(({ pattern }) => pattern.test(schedule));
  if (!dateFormat) {
    return IMPORT_ERRORS.INVALID_DATE_FORMAT;
  }
  // Without a valid zone the moment can't be placed; the timezone cell carries that error.
  if (!isValidTimezone(row.schedule_timezone)) {
    return null;
  }
  const scheduled = moment.tz(schedule, dateFormat.format, true, row.schedule_timezone);
  if (!scheduled.isValid()) {
    return IMPORT_ERRORS.INVALID_DATE;
  }
  return scheduled.valueOf() > now ? null : IMPORT_ERRORS.PAST_DATE;
};

// Any Date & Time typed, even one that doesn't parse (that cell has its own error).
const isScheduled = (row) => !!trim(row.origin_schedule_time);

// Sum of `count(id)` over the ids, or null when any of them has no known count.
const sumCounts = (ids, count) =>
  ids.reduce((total, id) => {
    const value = count(id);
    return total === null || typeof value !== 'number' ? null : total + value;
  }, 0);

// The row's reach through its selected pools and crews: `pool_driver_count`, the pools' drivers in the
// row's regions, and `crew_driver_count`, the crews' drivers. Nothing selected counts 0; null while a
// selected pool or crew has no known count (pools or that region's crews not loaded yet).
export const getRowDriverCounts = (row, { poolDriversById, crewsById } = {}) => {
  const regions = splitList(row.driver_regions).map((code) => code.toUpperCase());
  const poolIds = toIdList(row.driver_pool_ids);
  const crewIds = toIdList(row.driver_crew_ids);
  return {
    pool_driver_count: sumCounts(poolIds, (id) => {
      const byRegion = poolDriversById && poolDriversById.get(id);
      return byRegion ? regions.reduce((total, code) => total + (byRegion.get(code) || 0), 0) : undefined;
    }),
    crew_driver_count: sumCounts(crewIds, (id) => {
      const crew = crewsById && crewsById.get(id);
      return crew ? crew.driverCount : undefined;
    }),
  };
};

export const GROUP_ID_FIELDS = ['driver_pool_ids', 'driver_crew_ids'];

// A pool or crew selected, an unmatched sheet name included (its cell has its own "not found" error).
export const hasSelectedGroups = (row) =>
  GROUP_ID_FIELDS.some((field) => toIdList(row[field]).length > 0 || toIdList(row[NOT_FOUND_FIELDS[field]]).length > 0);

const NO_DRIVERS_ERRORS = { driver_pool_ids: IMPORT_ERRORS.POOL_NO_DRIVERS, driver_crew_ids: IMPORT_ERRORS.CREW_NO_DRIVERS };

// Who a scheduled row reaches, for the Driver IDs, Pool and Crew fields. Driver IDs settle it. With
// neither IDs nor a pool or crew, all three are required (a region alone isn't enough). Otherwise the
// selected pools and crews must have drivers between them; if not, each of Pool and Crew that has a
// selection says so. While a count needed to decide is unknown, the server's verdict stands.
const validateDriverReach = (row, field, context) => {
  if (!isScheduled(row) || hasDriverIds(row)) {
    return null;
  }
  if (!hasSelectedGroups(row)) {
    return IMPORT_ERRORS.DRIVER_IDS_REQUIRED;
  }
  if (field === 'drivers' || !toIdList(row[field]).length) {
    return null;
  }
  const { pool_driver_count: pools, crew_driver_count: crews } = getRowDriverCounts(row, context);
  if (pools > 0 || crews > 0 || pools === null || crews === null) {
    return null;
  }
  return NO_DRIVERS_ERRORS[field];
};

const validateDriverIds = (row, context) =>
  parseDriverIds(row.drivers).invalid.length ? IMPORT_ERRORS.INVALID_DRIVER_IDS : validateDriverReach(row, 'drivers', context);

// Pool and Crew are disabled without a region (their options depend on it), so they show no errors;
// the Region cell says a region is required while any pool or crew is selected.
const isGroupDisabled = (row, field) => GROUP_ID_FIELDS.includes(field) && !splitList(row.driver_regions).length;

// "Content contains HTML that is not allowed: <iframe>, <script>. Remove it and try again."
export const getBlockedHtmlMessage = (tags) =>
  `${IMPORT_ERRORS.BLOCKED_HTML}`.replace('{tags}', tags.map((tag) => `<${tag}>`).join(', '));

const validateBlockedHtml = (content) => {
  const tags = uniq([...String(content).matchAll(BLOCKED_HTML_PATTERN)].map((match) => match[1].toLowerCase()));
  return tags.length ? getBlockedHtmlMessage(tags) : null;
};

// Client-side rules only. Use getFieldError for what a cell should show.
export const validateImportField = (row, field, context = {}) => {
  const { now = Date.now(), regionCodes, crewsById } = context;
  if (isGroupDisabled(row, field)) {
    return null;
  }
  switch (field) {
    case 'media_type':
      return CHANNELS.includes(row.media_type) ? null : IMPORT_ERRORS.INVALID_CHANNEL;
    case 'is_promotional':
      return typeof row.is_promotional === 'boolean' ? null : IMPORT_ERRORS.INVALID_PROMOTIONAL;
    case 'name':
      return trim(row.name) ? null : IMPORT_ERRORS.NAME_REQUIRED;
    case 'subject':
      if (!isSubjectApplicable(row.media_type) || trim(row.subject)) {
        return null;
      }
      return row.media_type === IMPORT_CHANNEL.MESSENGER ? IMPORT_ERRORS.TITLE_REQUIRED : IMPORT_ERRORS.SUBJECT_REQUIRED;
    case 'communication':
      return trim(row.communication) ? validateBlockedHtml(row.communication) : IMPORT_ERRORS.CONTENT_REQUIRED;
    case 'origin_schedule_time':
      return validateSchedule(row, now);
    case 'schedule_timezone':
      if (!trim(row.schedule_timezone)) {
        return IMPORT_ERRORS.TIMEZONE_REQUIRED;
      }
      return isValidTimezone(row.schedule_timezone) ? null : IMPORT_ERRORS.INVALID_TIMEZONE;
    case 'drivers':
      return validateDriverIds(row, context);
    // Required for a driver pool or crew, otherwise optional; every code must be a known region.
    case 'driver_regions':
      return validateDriverRegions(row, regionCodes);
    // Pools aren't region-specific, so they aren't checked against the row's regions.
    case 'driver_pool_ids':
      return validateNotFound(row, 'driver_pool_ids', IMPORT_ERRORS.POOL_NOT_FOUND) || validateDriverReach(row, 'driver_pool_ids', context);
    case 'driver_crew_ids':
      return (
        validateNotFound(row, 'driver_crew_ids', IMPORT_ERRORS.CREW_NOT_FOUND) ||
        validateOutsideRegions(row, 'driver_crew_ids', crewsById, IMPORT_ERRORS.CREW_OUTSIDE_REGIONS) ||
        validateDriverReach(row, 'driver_crew_ids', context)
      );
    default:
      return null;
  }
};

// All of the API's remaining messages for a field, under its own key or any alias.
const getServerMessages = (serverErrors, field) => (serverErrors ? getErrorKeys(field).flatMap((key) => serverErrors[key] || []) : []);

export const getFieldErrors = (row, field, { validate = field, ...context } = {}) => {
  if (isGroupDisabled(row, field)) {
    return [];
  }
  const clientError = validate && validateImportField(row, validate, context);
  return clientError ? [clientError] : uniq(getServerMessages(context.serverErrors, field));
};

// The one message a cell leads with (border, placeholder): the first of getFieldErrors.
export const getFieldError = (row, field, context) => getFieldErrors(row, field, context)[0] || null;

const hasServerErrors = (serverErrors) => !!serverErrors && Object.values(serverErrors).some((messages) => messages && messages.length);

// API errors no cell can show (row-level, or for a field without a column), for the status chip.
export const getUnplacedServerErrors = (serverErrors) =>
  Object.keys(serverErrors || {})
    .filter((key) => !COLUMN_ERROR_KEYS.includes(key))
    .flatMap((key) => serverErrors[key] || []);

export const getImportRowErrors = (row, context) =>
  IMPORT_VALIDATED_FIELDS.reduce((errors, field) => {
    const error = validateImportField(row, field, context);
    return error ? { ...errors, [field]: error } : errors;
  }, {});

export const getImportRowStatus = (row, context = {}) =>
  IMPORT_VALIDATED_FIELDS.some((field) => validateImportField(row, field, context)) || hasServerErrors(context.serverErrors)
    ? IMPORT_ROW_STATUS.FIX
    : IMPORT_ROW_STATUS.READY;

export const isImportRowReady = (row, context) => getImportRowStatus(row, context) === IMPORT_ROW_STATUS.READY;

export const getScheduleTime = (row) => {
  const schedule = trim(row.origin_schedule_time);
  const dateFormat = schedule && DATE_FORMATS.find(({ pattern }) => pattern.test(schedule));
  if (!dateFormat || !isValidTimezone(row.schedule_timezone)) {
    return null;
  }
  const scheduled = moment.tz(schedule, dateFormat.format, true, row.schedule_timezone);
  return scheduled.isValid() ? scheduled.valueOf() : null;
};

// Transform to send to bulk create
export const toImportPayload = (row, { regionCodes } = {}) => ({
  ...omit(row, Object.values(NOT_FOUND_FIELDS)),
  drivers: parseDriverIds(row.drivers).ids.map(Number),
  driver_regions: parseRegions(row.driver_regions, regionCodes).codes,
  driver_pool_ids: toIdList(row.driver_pool_ids),
  driver_crew_ids: toIdList(row.driver_crew_ids),
  schedule_time: getScheduleTime(row),
});
